import React, { useState, useEffect, useCallback, useRef } from 'react';
import { HelpCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import MessageConsole from './MessageConsole';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

/**
 * SupportChatButton - Floating support chat button for all user roles
 * 
 * For Customer: Chat with Calendar Admin (support messages only)
 * For Calendar Admin: Chat with Super Admin (support messages only)
 * For Super Admin: View/respond to support messages from Calendar Admins
 * 
 * Props:
 * - autoOpen: boolean - if true, automatically open the support chat
 * - onOpened: function - callback when chat is opened
 */
const SupportChatButton = ({ autoOpen = false, onOpened }) => {
  const { runningUser, getAuthHeaders } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showConsole, setShowConsole] = useState(false);
  const [supportPartner, setSupportPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pulse, setPulse] = useState(false);
  
  const fetchingRef = useRef(false);
  const mountedRef = useRef(true);
  const autoOpenTriggered = useRef(false);
  const prevCountRef = useRef(0);
  
  const userRole = runningUser?.role;

  // Handle auto-open from Header Support button click
  useEffect(() => {
    if (autoOpen && supportPartner && !autoOpenTriggered.current) {
      autoOpenTriggered.current = true;
      setTimeout(() => {
        setShowConsole(true);
        if (onOpened) onOpened();
      }, 0);
    }
  }, [autoOpen, supportPartner, onOpened]);

  // Listen for custom events from Header
  useEffect(() => {
    const handleOpenSupport = () => {
      // For super admin, always open (supportPartner might be placeholder)
      // For others, check if supportPartner exists
      if (userRole === 'super_admin' || supportPartner) {
        setShowConsole(true);
      }
    };
    
    // Different event per role
    const eventName = userRole === 'customer' 
      ? 'openCustomerSupportChat'
      : userRole === 'calendar_admin'
        ? 'openCalendarAdminSupportChat'
        : 'openSuperAdminSupportChat';
    
    window.addEventListener(eventName, handleOpenSupport);
    return () => window.removeEventListener(eventName, handleOpenSupport);
  }, [userRole, supportPartner]);

  // Fetch the support partner based on role
  const fetchSupportPartner = useCallback(async () => {
    if (!runningUser?.id || fetchingRef.current) {
      setLoading(false);
      return;
    }
    
    fetchingRef.current = true;
    
    try {
      if (userRole === 'customer') {
        // Customer -> Get their Calendar Admin
        const response = await axios.get(`${API_BASE_URL}/messages/chat-partners`, {
          headers: getAuthHeaders()
        });
        
        if (mountedRef.current && response.data.success) {
          // Find the calendar admin from partners
          const calendarAdmin = response.data.data?.find(p => p.role === 'calendar_admin');
          if (calendarAdmin) {
            setSupportPartner(calendarAdmin);
          }
        }
      } else if (userRole === 'calendar_admin') {
        // Calendar Admin -> Get Super Admin
        const response = await axios.get(`${API_BASE_URL}/messages/chat-partners`, {
          headers: getAuthHeaders()
        });
        
        if (mountedRef.current && response.data.success) {
          const superAdmin = response.data.data?.find(p => p.role === 'super_admin');
          if (superAdmin) {
            setSupportPartner(superAdmin);
          }
        }
      } else if (userRole === 'super_admin') {
        // Super Admin -> Can receive support from any Calendar Admin
        // For now, we'll use a placeholder - the actual partner will be selected from a list
        setSupportPartner({ 
          id: 'support_inbox', 
          full_name: 'Support Inbox',
          role: 'support_inbox'
        });
      }
    } catch (err) {
      console.error('Error fetching support partner:', err);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        fetchingRef.current = false;
      }
    }
  }, [runningUser?.id, userRole, getAuthHeaders]);

  // Fetch unread support message count
  const fetchUnreadCount = useCallback(async () => {
    if (!runningUser?.id) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/messages/unread/count`, {
        params: { message_type: 'support' },
        headers: getAuthHeaders()
      });
      
      if (mountedRef.current && response.data.success) {
        const newCount = response.data.unread_count || 0;
        
        // Pulse animation if count increased
        if (newCount > prevCountRef.current && prevCountRef.current > 0) {
          setPulse(true);
          setTimeout(() => setPulse(false), 1000);
        }
        
        prevCountRef.current = newCount;
        setUnreadCount(newCount);
      }
    } catch (err) {
      console.error('Error fetching support unread count:', err);
    }
  }, [runningUser?.id, getAuthHeaders]);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchSupportPartner();
    fetchUnreadCount();
    
    // Poll for unread count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [fetchSupportPartner, fetchUnreadCount]);

  // Refresh count when console closes
  const handleConsoleClose = () => {
    setShowConsole(false);
    fetchUnreadCount();
  };

  // Don't render if no user or still loading
  if (!runningUser || loading) return null;

  // Don't render the floating button - it's integrated into Header
  // Only render the MessageConsole when open
  
  return (
    <>
      {/* Support Message Console */}
      {showConsole && supportPartner && supportPartner.id !== 'support_inbox' && (
        <MessageConsole
          customer={supportPartner}
          isOpen={showConsole}
          onClose={handleConsoleClose}
          messageType="support"
        />
      )}
      
      {/* For Super Admin - Support Inbox (list of Calendar Admins with support messages) */}
      {showConsole && userRole === 'super_admin' && (
        <SupportInboxConsole
          isOpen={showConsole}
          onClose={handleConsoleClose}
          getAuthHeaders={getAuthHeaders}
        />
      )}
    </>
  );
};

/**
 * SupportInboxConsole - For Super Admin to view ALL support messages from Calendar Admins
 * 
 * Private Message Channel Flow:
 * 1. Support is initiated by source (Calendar Admin sends support message)
 * 2. Host (Super Admin) sees pink Support icon indicating unread messages
 * 3. Host opens Support Inbox - sees all support conversations
 * 4. Host double-clicks on inbound message → Creates private channel with that source
 * 5. All replies from host are sent to that source within the channel
 * 6. Host double-clicks any message from same source again → Closes the channel
 */
const SupportInboxConsole = ({ isOpen, onClose, getAuthHeaders }) => {
  const [allMessages, setAllMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [calendarAdmins, setCalendarAdmins] = useState({});  // Map of id -> admin info
  const [loading, setLoading] = useState(true);
  
  // Private Channel State
  const [activeChannel, setActiveChannel] = useState(null);  // The source user for active channel
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Fetch all support messages
  useEffect(() => {
    const fetchSupportMessages = async () => {
      if (!isOpen) return;
      setLoading(true);
      
      try {
        // Get all calendar admins first (for name lookup)
        const adminsResponse = await axios.get(`${API_BASE_URL}/messages/chat-partners`, {
          headers: getAuthHeaders()
        });
        
        const adminsMap = {};
        if (adminsResponse.data.success) {
          adminsResponse.data.data?.forEach(admin => {
            if (admin.role === 'calendar_admin') {
              adminsMap[admin.id] = admin;
            }
          });
        }
        setCalendarAdmins(adminsMap);
        
        // Get all support messages from all calendar admins
        const allSupportMessages = [];
        for (const adminId of Object.keys(adminsMap)) {
          try {
            const msgResponse = await axios.get(`${API_BASE_URL}/messages/${adminId}`, {
              params: { message_type: 'support', limit: 100 },
              headers: getAuthHeaders()
            });
            
            if (msgResponse.data.success) {
              const messages = msgResponse.data.data.messages || [];
              messages.forEach(msg => {
                msg._adminId = adminId;
                msg._adminName = adminsMap[adminId]?.full_name || 'Unknown';
              });
              allSupportMessages.push(...messages);
            }
          } catch (err) {
            console.error(`Error fetching messages from admin ${adminId}:`, err);
          }
        }
        
        // Sort by timestamp (newest first)
        allSupportMessages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        setAllMessages(allSupportMessages);
        setFilteredMessages(allSupportMessages);
      } catch (err) {
        console.error('Error fetching support inbox:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSupportMessages();
  }, [isOpen, getAuthHeaders]);

  // Apply channel filter
  useEffect(() => {
    if (activeChannel) {
      setFilteredMessages(allMessages.filter(msg => 
        msg.sender_id === activeChannel.id || msg.recipient_id === activeChannel.id
      ));
    } else {
      setFilteredMessages(allMessages);
    }
  }, [activeChannel, allMessages]);

  // Handle double-click to toggle private channel
  const handleDoubleClick = (msg) => {
    // Determine the source (Calendar Admin) from the message
    const sourceId = msg.direction === 'inbound' ? msg.sender_id : msg.recipient_id;
    const sourceAdmin = calendarAdmins[sourceId];
    
    if (!sourceAdmin) return;
    
    // Toggle channel: if already connected to this source, disconnect
    if (activeChannel && activeChannel.id === sourceId) {
      // Close the channel
      setActiveChannel(null);
      setNewMessage('');
    } else {
      // Open/switch to this channel
      setActiveChannel(sourceAdmin);
    }
  };

  // Send message in active channel
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeChannel) return;
    
    setSending(true);
    try {
      await axios.post(
        `${API_BASE_URL}/messages/${activeChannel.id}`,
        {
          content: newMessage,
          channel: 'yibooking',
          message_type: 'support'
        },
        { headers: getAuthHeaders() }
      );
      
      setNewMessage('');
      
      // Refresh messages for this channel
      const msgResponse = await axios.get(`${API_BASE_URL}/messages/${activeChannel.id}`, {
        params: { message_type: 'support', limit: 100 },
        headers: getAuthHeaders()
      });
      
      if (msgResponse.data.success) {
        const newMsgs = msgResponse.data.data.messages || [];
        newMsgs.forEach(msg => {
          msg._adminId = activeChannel.id;
          msg._adminName = activeChannel.full_name;
        });
        
        // Update allMessages - replace messages from this admin
        setAllMessages(prev => {
          const filtered = prev.filter(m => m._adminId !== activeChannel.id);
          const merged = [...filtered, ...newMsgs];
          merged.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          return merged;
        });
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className={`p-4 border-b flex items-center justify-between flex-shrink-0 ${
          activeChannel ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'
        }`}>
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <HelpCircle className="w-5 h-5" />
              {activeChannel ? (
                <>
                  Private Channel: {activeChannel.full_name}
                </>
              ) : (
                'Support Inbox'
              )}
            </h2>
            {activeChannel && (
              <p className="text-sm opacity-90 mt-1">
                Double-click any message to close channel
              </p>
            )}
          </div>
          <button 
            onClick={onClose}
            className={`p-1 rounded text-xl ${
              activeChannel ? 'hover:bg-green-600' : 'hover:bg-yellow-600'
            }`}
          >
            ✕
          </button>
        </div>
        
        {/* Channel Status Banner */}
        {activeChannel && (
          <div className="px-4 py-2 bg-green-100 text-green-800 text-sm flex items-center justify-between border-b border-green-200">
            <span>
              🔗 Connected to <strong>{activeChannel.full_name}</strong> ({activeChannel.email})
            </span>
            <button 
              onClick={() => setActiveChannel(null)}
              className="text-green-600 hover:text-green-800 underline text-xs"
            >
              Close Channel
            </button>
          </div>
        )}
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading support messages...</div>
          ) : filteredMessages.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {activeChannel ? 'No messages in this channel' : 'No support messages'}
            </div>
          ) : (
            filteredMessages.map((msg) => {
              const isFromActiveChannel = activeChannel && 
                (msg.sender_id === activeChannel.id || msg.recipient_id === activeChannel.id);
              
              return (
                <div
                  key={msg.id}
                  onDoubleClick={() => handleDoubleClick(msg)}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    msg.direction === 'inbound' 
                      ? `bg-blue-50 hover:bg-blue-100 border-l-4 ${
                          isFromActiveChannel ? 'border-green-500' : 'border-blue-400'
                        }` 
                      : `bg-green-50 hover:bg-green-100 border-l-4 border-green-400 ml-8`
                  } ${isFromActiveChannel ? 'ring-2 ring-green-300' : ''}`}
                  title={activeChannel 
                    ? "Double-click to close channel" 
                    : "Double-click to open private channel with this user"
                  }
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-medium ${
                      msg.direction === 'inbound' ? 'text-blue-700' : 'text-green-700'
                    }`}>
                      {msg.direction === 'inbound' ? (
                        <>
                          {msg._adminName}
                          {isFromActiveChannel && (
                            <span className="ml-2 text-xs bg-green-200 text-green-700 px-1.5 py-0.5 rounded">
                              Channel
                            </span>
                          )}
                        </>
                      ) : 'You'}
                    </span>
                    <span className="text-xs text-gray-500">{formatTime(msg.timestamp)}</span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.content}</p>
                  {!msg.read_at && msg.direction === 'inbound' && (
                    <span className="text-xs text-blue-600 font-medium">● New</span>
                  )}
                </div>
              );
            })
          )}
        </div>
        
        {/* Message Input - only when channel is active */}
        {activeChannel ? (
          <form onSubmit={handleSendMessage} className="p-4 border-t bg-white flex-shrink-0">
            <div className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={`Message ${activeChannel.full_name}...`}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                disabled={sending}
                autoFocus
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || sending}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? '...' : 'Send'}
              </button>
            </div>
          </form>
        ) : (
          /* Hint when no channel is active */
          !loading && filteredMessages.length > 0 && (
            <div className="p-3 border-t bg-gray-100 text-center text-sm text-gray-500 flex-shrink-0">
              💡 Double-click on an inbound message to open a private channel and reply
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default SupportChatButton;
